import { ChevronDown, ChevronUp, ChevronsUpDown, ExternalLink, Search, Square, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/status-badge";
import { adminApi, AdminService } from "../../lib/adminApi";
import { notify } from "../../lib/notify";

type SortField = "name" | "status" | "createdAt";
type SortOrder = "asc" | "desc";

function formatDate(d?: string) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString() + " " + dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function SortIcon({ field, sortField, sortOrder }: { field: SortField; sortField: SortField; sortOrder: SortOrder }) {
  if (field !== sortField) return <ChevronsUpDown className="inline ml-1 h-3 w-3 opacity-40" />;
  return sortOrder === "asc"
    ? <ChevronUp className="inline ml-1 h-3 w-3" />
    : <ChevronDown className="inline ml-1 h-3 w-3" />;
}

export default function AdminServicesPage() {
  const [services, setServices] = useState<AdminService[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerInput, setOwnerInput] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectInput, setProjectInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = (p = page, lim = limit) => {
    setLoading(true);
    adminApi
      .listServices({
        page: p,
        limit: lim,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        search: search || undefined,
        ownerEmail: ownerEmail || undefined,
        projectName: projectName || undefined,
        sort: sortField,
        order: sortOrder,
      })
      .then((res) => {
        setServices(res.services);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); setPage(1); }, [statusFilter, typeFilter, search, ownerEmail, projectName, sortField, sortOrder, limit]); // eslint-disable-line

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(o => o === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") setSearch(searchInput);
  };

  const handleOwnerSubmit = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") setOwnerEmail(ownerInput);
  };

  const handleProjectSubmit = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") setProjectName(projectInput);
  };

  const handleServiceClick = (s: AdminService) => {
    const serviceId = s._id ?? s.id ?? "";
    const projectId = s.projectId ?? "";
    if (projectId && serviceId) {
      window.open(`/projects/${projectId}/services/${serviceId}`, "_blank");
    }
  };

  const handleStop = async (svc: AdminService) => {
    const id = svc._id ?? svc.id ?? "";
    setActionLoading(id);
    try {
      await adminApi.stopService(id);
      notify.success(`${svc.name} stopped`);
      setServices(prev => prev.map(s => (s._id ?? s.id) === id ? { ...s, status: "stopped" } : s));
    } catch (err: any) {
      notify.error(err?.message ?? "Stop failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (svc: AdminService) => {
    const id = svc._id ?? svc.id ?? "";
    setActionLoading(id);
    try {
      await adminApi.deleteService(id);
      notify.success(`${svc.name} deleted`);
      setServices(prev => prev.filter(s => (s._id ?? s.id) !== id));
      setTotal(t => t - 1);
    } catch (err: any) {
      notify.error(err?.message ?? "Delete failed");
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const thClass = "px-4 py-3 text-left font-medium cursor-pointer select-none hover:text-foreground transition-colors";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-h2 tracking-tight">Services</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{total} total services</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search name… (Enter)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchSubmit}
            onBlur={() => setSearch(searchInput)}
            className="pl-8 w-56 h-9"
          />
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Owner email… (Enter)"
            value={ownerInput}
            onChange={(e) => setOwnerInput(e.target.value)}
            onKeyDown={handleOwnerSubmit}
            onBlur={() => setOwnerEmail(ownerInput)}
            className="pl-8 w-56 h-9"
          />
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Project name… (Enter)"
            value={projectInput}
            onChange={(e) => setProjectInput(e.target.value)}
            onKeyDown={handleProjectSubmit}
            onBlur={() => setProjectName(projectInput)}
            className="pl-8 w-48 h-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="running">Running</option>
          <option value="building">Building</option>
          <option value="failed">Failed</option>
          <option value="stopped">Stopped</option>
          <option value="pending">Pending</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value="">All types</option>
          <option value="web">Web</option>
          <option value="worker">Worker</option>
          <option value="cron">Cron</option>
          <option value="database">Database</option>
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-md bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className={thClass} onClick={() => handleSort("name")}>
                Name <SortIcon field="name" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium">Type</th>
              <th className={thClass} onClick={() => handleSort("status")}>
                Status <SortIcon field="status" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium">Framework</th>
              <th className="px-4 py-3 text-left font-medium">Created by</th>
              <th className={thClass} onClick={() => handleSort("createdAt")}>
                Created <SortIcon field="createdAt" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : services.length === 0
              ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No services found.
                  </td>
                </tr>
              )
              : services.map((s) => {
                  const id = s._id ?? s.id ?? "";
                  const busy = actionLoading === id;
                  return (
                    <tr
                      key={id}
                      className="hover:bg-muted/40 transition-colors cursor-pointer"
                      onClick={() => handleServiceClick(s)}
                    >
                      <td className="px-4 py-3 font-medium">
                        <span className="flex items-center gap-1.5">
                          {s.name}
                          {s.projectId && <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {s.type && <Badge variant="outline" className="text-xs capitalize">{s.type}</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge kind="service" status={s.status ?? "unknown"} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{s.framework ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{s.createdByEmail ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(s.createdAt)}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {/* Stop */}
                          {s.status === "running" || s.status === "building" ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-muted-foreground hover:text-warning"
                                  title="Stop service"
                                  disabled={busy}
                                >
                                  <Square className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Stop service?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    <strong>{s.name}</strong> will be stopped and its Kubernetes workload uninstalled. The service record is preserved and can be redeployed.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleStop(s)}>Stop</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : null}
                          {/* Delete */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                title="Delete service"
                                disabled={busy}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete service?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{s.name}</strong> and all its deployments. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => handleDelete(s)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Page {page} of {totalPages} · {total} services</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => { const p = page - 1; setPage(p); load(p); }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => { const p = page + 1; setPage(p); load(p); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
